<template>
  <!--
    Dedicated layout for auth pages (login, register, logout).

    Why a separate layout:
    Previously these pages used the default layout with v-if conditionals
    that hid the sidebar and header on auth routes. That worked when the
    user navigated directly to /login, but it broke on the redirect path:
    a user hitting a protected route (e.g. /dashboards/overview) while
    unauthenticated had the page SSR-rendered WITH the sidebar (since
    auth.global.ts skips on the server), then the client-side middleware
    redirected to /login and Vue removed the sidebar reactively. The
    resulting hydration mismatch left the centered login form
    left-aligned until the user refreshed.

    Splitting auth pages onto their own layout means there are no
    auth-specific conditionals in the default layout — the layout swap
    is handled by Nuxt's <NuxtLayout> at the route boundary, which
    reflows cleanly without the stuck-flexbox bug.
  -->
  <div class="min-h-screen font-sans text-app-primary dark">
    <!-- Background — auth pages always render dark, with the nebula
         overlay. CSS vars resolve to the dark palette because the
         wrapper carries the `dark` class unconditionally. -->
    <div class="fixed inset-0 z-0 pointer-events-none overflow-hidden surface-app">
      <div class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay"></div>
      <img
        src="~/assets/images/background.png"
        alt="Nebula Background"
        class="absolute inset-0 w-full h-full object-cover animate-breathe"
        :style="{ opacity: 'var(--nebula-opacity, 0.4)' }"
      />
    </div>

    <!-- Content. Plain centered viewport — pages own their inner
         layout (login form, register wizard, logout spinner). -->
    <main class="relative z-10 min-h-screen flex items-center justify-center w-full">
      <slot />
    </main>
  </div>
</template>

<script setup lang="ts">
// Force the `dark` class onto <html> while the auth layout is mounted
// so the theme variables resolve to the dark palette regardless of the
// user's persisted light-variant preference. Mirrors the behavior the
// default layout had via its route.path watcher; restored on unmount
// so navigating into the app drops the user back into their chosen
// theme without a manual refresh.
const { themeMode, lightVariant } = useTheme()
const LIGHT_CLASSES = ['light-pearl', 'light-aurora', 'light-glacier', 'light-twilight', 'light-sage', 'light-latte']

onMounted(() => {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  for (const c of LIGHT_CLASSES) html.classList.remove(c)
  html.classList.add('dark')
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  for (const c of LIGHT_CLASSES) html.classList.remove(c)
  if (themeMode.value === 'dark') {
    html.classList.add('dark')
  } else {
    html.classList.remove('dark')
    html.classList.add(`light-${lightVariant.value}`)
  }
})
</script>
